/**
 * Music to Motion — client-side audio analysis for beat-synced animations.
 * Uses Web Audio API exclusively — no server, no external libraries.
 *
 * Strategy: decode the audio file → compute a low-pass energy envelope
 * → detect peaks (beats) by finding local maxima above a rolling threshold.
 * Return beat timestamps (ms) which the caller uses to position animations.
 */

export async function analyzeAudio(file) {
  const arrayBuffer = await file.arrayBuffer();
  // Web Audio context (any sample rate — we'll use whatever the file provides)
  const AudioCtx = window.AudioContext || window.webkitAudioContext;
  if (!AudioCtx) throw new Error("Web Audio not supported in this browser");
  const ctx = new AudioCtx();
  const buffer = await ctx.decodeAudioData(arrayBuffer);
  const duration = buffer.duration; // seconds
  const sampleRate = buffer.sampleRate;

  // Downsample to mono, then compute energy in 256-sample windows.
  const ch = buffer.getChannelData(0);
  const winSize = Math.floor(sampleRate * 0.023); // ~23ms windows (≈43 per sec)
  const energies = [];
  for (let i = 0; i < ch.length; i += winSize) {
    let e = 0;
    const end = Math.min(i + winSize, ch.length);
    for (let j = i; j < end; j++) e += ch[j] * ch[j];
    energies.push(e / winSize);
  }

  // Rolling mean threshold for peak detection (window of ~1 sec = 43 windows)
  const W = 43;
  const peaks = [];
  for (let i = W; i < energies.length - 1; i++) {
    let mean = 0;
    for (let k = i - W; k < i; k++) mean += energies[k];
    mean /= W;
    const e = energies[i];
    // A peak is energy ≥ 1.55× rolling mean AND a local maximum
    if (e > mean * 1.55 && e >= energies[i - 1] && e >= energies[i + 1]) {
      peaks.push((i * winSize / sampleRate) * 1000); // ms
    }
  }

  // De-bounce peaks closer than ~140ms (beat min interval → ~428 BPM ceiling)
  const beats = [];
  let last = -Infinity;
  for (const p of peaks) {
    if (p - last >= 140) { beats.push(p); last = p; }
  }

  // Rough BPM from median inter-onset interval
  const bpm = beats.length >= 4 ? Math.round(60000 / median(diffs(beats))) : null;

  try { ctx.close(); } catch {}

  return {
    duration_ms: Math.round(duration * 1000),
    bpm,
    beats,     // ms timestamps
    count: beats.length,
    filename: file.name,
  };
}

function diffs(arr) { return arr.slice(1).map((v, i) => v - arr[i]); }
function median(arr) {
  const s = arr.slice().sort((a, b) => a - b);
  return s[Math.floor(s.length / 2)];
}

/**
 * Build animations that sync with detected beats.
 * Heuristic:
 *   - Headlines/textboxes appear on beat 1, 3, 5… (emphasized beats)
 *   - Rounded-rect (CTA pill) bounces on the LAST beat before duration ends (drop)
 *   - Circles zoom-in on beats 2, 4, 6… (off-beats)
 *   - Other shapes/images fade-in on remaining beats
 */
export function buildBeatSyncAnimations(objs, beats, duration_ms) {
  if (!objs?.length || !beats?.length) return [];
  const anims = [];
  const lastBeat = beats[beats.length - 1];

  objs.forEach((o, i) => {
    // Pick a beat based on object index, wrapping if we run out
    const beat = beats[i % beats.length];
    const isRoundedRect = o.type === "rect" && (o.rx > 8 || o.ry > 8);
    const isHeadline    = o.type === "textbox" && (o.fontSize ?? 0) > 40;
    const isCircle      = o.type === "circle";

    let type;
    let startAt = beat;
    let dur = 600;

    if (isRoundedRect) {
      // CTA → bounce on the latest beat (drop)
      type = "bounce";
      startAt = Math.max(0, lastBeat - 200);
      dur = 700;
    } else if (isHeadline) {
      type = "typewriter";
      dur = Math.min(1400, Math.max(400, (o.text?.length || 0) * 60));
    } else if (isCircle) {
      type = "zoom-in";
      dur = 500;
    } else if (o.type === "rect") {
      type = "slide-in-right";
      dur = 500;
    } else {
      type = "fade-in";
      dur = 650;
    }

    // Clamp so startAt + duration never exceeds duration_ms
    const clamped = Math.min(startAt, Math.max(0, duration_ms - dur - 50));
    anims.push({ object_index: i, type, start_at: Math.round(clamped), duration: Math.round(dur) });
  });

  // Sort by start_at for a tidy timeline
  anims.sort((a, b) => a.start_at - b.start_at);
  return anims;
}
